import { useState, useEffect, useCallback } from 'react';
import { useAuth } from './useAuth';
import { useToast } from './useToast';
import api from '../utils/api';

/**
 * Custom hook for handling subscription-related operations
 * @returns {Object} Subscription methods and state
 */
export const useSubscription = () => {
  const { user, refreshUser } = useAuth();
  const { showToast } = useToast();
  
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  /**
   * Fetch the current user's subscription
   */
  const fetchSubscription = useCallback(async () => {
    if (!user) {
      setSubscription(null);
      setLoading(false);
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      // For school admins, use a simplified approach
      if (user.role === 'school_admin') {
        const schoolResponse = await api.get('/schools/dashboard');
        
        if (schoolResponse.data?.success && schoolResponse.data.data?.subscription) {
          const schoolSubData = schoolResponse.data.data.subscription;
          
          // Check if end date is in the future to determine if active
          const now = new Date();
          const endDate = schoolSubData.endDate ? new Date(schoolSubData.endDate) : null;
          const isActive = endDate && endDate > now;
          
          setSubscription({
            tier: schoolSubData.tier || 'ignite',
            isActive: isActive,
            userType: 'school',
            billingCycle: 'annual',
            startDate: schoolSubData.startDate || new Date().toISOString(),
            endDate: schoolSubData.endDate || new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString(),
            schoolId: schoolResponse.data.data.schoolInfo?._id || schoolResponse.data.data.schoolInfo?.schoolId
          });
        } else {
          // Default to ignite tier for schools with no subscription
          setSubscription({
            tier: 'ignite',
            isActive: true,
            userType: 'school',
            billingCycle: 'annual',
            startDate: new Date().toISOString(),
            endDate: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString()
          });
        }
      } 
      // For parent users
      else if (user.role === 'parent') {
        // Try to get a real subscription first
        const response = await api.get('/subscriptions/me');
        
        if (response.data?.success && response.data.data) {
          const subData = response.data.data;
          const now = new Date();
          const endDate = subData.endDate ? new Date(subData.endDate) : null;
          const isActive = endDate && endDate > now;
          
          setSubscription({
            ...subData,
            isActive: isActive,
            userType: 'parent'
          });
        } else {
          // Default free tier for parents
          setSubscription({
            tier: 'free',
            isActive: true,
            userType: 'parent',
            billingCycle: 'annual',
            startDate: new Date().toISOString(),
            endDate: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString()
          });
        }
      }
      // Other user types
      else {
        const response = await api.get('/subscriptions/me');
        
        if (response.data?.success && response.data.data) {
          const subData = response.data.data;
          const now = new Date();
          const endDate = subData.endDate ? new Date(subData.endDate) : null;
          const isActive = endDate && endDate > now;
          
          setSubscription({
            ...subData,
            isActive: isActive,
            userType: user.role === 'teacher' ? 'teacher' : 'user'
          });
        } else {
          setSubscription(null);
        }
      }
    } catch (err) {
      setError('Failed to load subscription data');
      
      // Default subscriptions for error cases
      if (user?.role === 'school_admin') {
        setSubscription({
          tier: 'ignite',
          isActive: true,
          userType: 'school',
          billingCycle: 'annual',
          startDate: new Date().toISOString(),
          endDate: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString()
        });
      } else if (user?.role === 'parent') {
        setSubscription({
          tier: 'free',
          isActive: true,
          userType: 'parent',
          billingCycle: 'annual',
          startDate: new Date().toISOString(),
          endDate: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString()
        });
      } else {
        setSubscription(null);
      }
    } finally {
      setLoading(false);
    }
  }, [user]);
  
  // Fetch subscription on mount and when user changes
  useEffect(() => {
    fetchSubscription();
  }, [fetchSubscription]);
  
  /**
   * Check if the current subscription is active
   * @returns {boolean} True if subscription is active
   */
  const isSubscriptionActive = useCallback(() => {
    if (!subscription) return false;
    return subscription.isActive === true;
  }, [subscription]);
  
  /**
   * Get the current subscription tier
   * @returns {string|null} Subscription tier or null if no subscription
   */
  const getCurrentTier = useCallback(() => {
    if (!subscription) return null;
    return subscription.tier || null;
  }, [subscription]);
  
  /**
   * Check if the current subscription is at or above a specified tier
   * @param {string|Array} requiredTiers - Tier or array of tiers to check against
   * @returns {boolean} True if current tier matches or exceeds required tier
   */
  const hasTier = useCallback((requiredTiers) => {
    if (!subscription || !subscription.isActive) return false;
    
    const currentTier = subscription.tier;
    
    if (Array.isArray(requiredTiers)) {
      return requiredTiers.includes(currentTier);
    }
    
    return currentTier === requiredTiers;
  }, [subscription]);
  
  /**
   * Get days remaining in the current subscription period
   * @returns {number} Days remaining or 0 if no subscription
   */
  const getDaysRemaining = useCallback(() => {
    if (!subscription || !subscription.isActive) return 0;
    
    const now = new Date();
    const endDate = new Date(subscription.endDate);
    const diffTime = Math.abs(endDate - now);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    return diffDays;
  }, [subscription]);
  
  /**
   * Check if subscription is expiring soon (within 7 days)
   * @returns {boolean} True if subscription is expiring soon
   */
  const isExpiringSoon = useCallback(() => {
    const daysRemaining = getDaysRemaining();
    return daysRemaining > 0 && daysRemaining <= 7;
  }, [getDaysRemaining]);
  
  /**
   * Cancel the current subscription
   * @returns {Promise} Result of the cancellation operation
   */
  const cancelSubscription = useCallback(async () => {
    if (!subscription) {
      showToast('No active subscription to cancel', 'error');
      return false;
    }
    
    try {
      setLoading(true);
      const response = await api.put(`/subscriptions/cancel`);
      
      if (response.data?.success) {
        showToast('Subscription successfully canceled', 'success');
        await fetchSubscription();
        await refreshUser();
        
        return true;
      } else {
        showToast(response.data?.message || 'Failed to cancel subscription', 'error');
        return false;
      }
    } catch (err) {
      console.error('Error cancelling subscription:', err);
      showToast(err.response?.data?.message || 'Failed to cancel subscription', 'error');
      return false;
    } finally {
      setLoading(false);
    }
  }, [subscription, showToast, fetchSubscription, refreshUser]);
  
  /**
   * Change the current subscription to a new tier
   * @param {string} newTier - The new subscription tier
   * @param {string} billingCycle - 'quarterly' or 'annual'
   * @returns {Promise} Result of the change operation
   */
  const changeSubscription = useCallback(async (newTier, billingCycle) => {
    try {
      setLoading(true);
      
      if (subscription?._id) {
        try {
          // First try to change subscription directly (for downgrades or same-price changes)
          const response = await api.put(`/subscriptions/renew`, {
            tier: newTier,
            billingCycle,
            paymentMethod: subscription.paymentMethod || 'card'
          });
          
          if (response.data?.success) {
            console.log('Successfully changed subscription without checkout');
            await fetchSubscription();
            await refreshUser();
            return { success: true, needsCheckout: false };
          }
        } catch (err) {
          // If direct change fails because payment is needed, return special response
          if (err.response?.status === 402 || 
              err.response?.data?.code === 'PAYMENT_REQUIRED' ||
              err.response?.data?.message?.includes('payment')) {
            console.log('Payment required for tier change');
            return { 
              success: true, 
              needsCheckout: true,
              message: 'Please complete payment to upgrade your subscription'
            };
          }
          
          // Otherwise rethrow the error
          throw err;
        }
      }
      
      // If no subscription yet or direct change not possible, proceed to checkout
      console.log('Proceeding to checkout for subscription change');
      return { 
        success: true, 
        needsCheckout: true 
      };
    } catch (err) {
      console.error('Error changing subscription:', err);
      const errorMessage = err.response?.data?.message || 'Failed to change subscription';
      showToast(errorMessage, 'error');
      return { 
        success: false, 
        message: errorMessage
      };
    } finally {
      setLoading(false);
    }
  }, [subscription, showToast, fetchSubscription, refreshUser]);
  
  return {
    subscription,
    loading,
    error,
    fetchSubscription,
    isSubscriptionActive,
    getCurrentTier,
    hasTier,
    getDaysRemaining,
    isExpiringSoon,
    cancelSubscription,
    changeSubscription
  };
};

export default useSubscription; 